(function () {
  'use strict';

  const PARAMETER_NAME = 'offer';

  // Shopify native discount cookie.
  //
  // Shopify sets this cookie after:
  //
  // /discount/TEST209?offer=kochpinzette
  //
  // We use it only when an "offer" URL parameter
  // is present.
  const SHOPIFY_DISCOUNT_COOKIE = 'discount_code';

  const COOKIE_NAME = 'shop_offer_channel';
  const COOKIE_DISCOUNT_NAME = 'shop_offer_discount';

  const CART_ATTRIBUTE_NAME = 'shop_offer_channel';
  const CART_ATTRIBUTE_DISCOUNT = 'shop_offer_discount';
  const CART_ATTRIBUTE_STARTED_AT =
    'shop_offer_channel_started_at';

  // --------------------------------------------------
  // COOKIE / CAMPAIGN LIFETIME
  // --------------------------------------------------

  // const COOKIE_MAX_AGE = 7 * 24 * 60 * 60; // 7 days
  const COOKIE_MAX_AGE =
    window.themeVariables.settings.utmCampaignCookieExpiration *
    24 *
    60 *
    60;

  // const CAMPAIGN_MAX_AGE = 1 * 60 * 1000; // 1 min
  const CAMPAIGN_MAX_AGE =
    window.themeVariables.settings.utmCampaignCookieExpiration *
    24 *
    60 *
    60 *
    1000;

  // --------------------------------------------------
  // LOCAL DEVELOPMENT
  // --------------------------------------------------

  const IS_LOCAL =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';


  // Used by the existing progress-bar fetch interceptor
  // so that our own cart attribute request is not treated
  // as a regular cart change.
  window.ShopOfferCartUpdating = false;


  // --------------------------------------------------
  // COOKIEBOT CONSENT
  // --------------------------------------------------

  function hasOfferCookieConsent() {
    if (IS_LOCAL) {
      return true;
    }

    return window.Cookiebot?.consent?.necessary === true;
  }


  // --------------------------------------------------
  // URL PARAMETER
  // --------------------------------------------------

  function getUrlParameter(name) {
    const params =
      new URLSearchParams(window.location.search);

    const value =
      params.get(name);

    return value
      ? value.trim()
      : null;
  }


  function getUrlOffer() {
    return getUrlParameter(
      PARAMETER_NAME
    );
  }


  // --------------------------------------------------
  // COOKIE
  // --------------------------------------------------

  function setCookie(
    name,
    value,
    maxAge
  ) {
    document.cookie =
      `${name}=${encodeURIComponent(value)};` +
      `Max-Age=${maxAge};` +
      `Path=/;` +
      `SameSite=Lax`;
  }


  function getCookie(name) {
    const cookies = document.cookie
      ? document.cookie.split('; ')
      : [];

    for (const cookie of cookies) {
      const separatorIndex =
        cookie.indexOf('=');

      if (separatorIndex === -1) {
        continue;
      }

      const key =
        cookie.slice(
          0,
          separatorIndex
        );

      const value =
        cookie.slice(
          separatorIndex + 1
        );

      if (key === name) {
        return decodeURIComponent(value);
      }
    }

    return null;
  }


  function deleteCookie(name) {
    document.cookie =
      `${name}=;` +
      `Max-Age=0;` +
      `Path=/;` +
      `SameSite=Lax`;
  }


  /*
   * Read campaign from our own cookies.
   *
   * IMPORTANT:
   * We deliberately DO NOT check CAMPAIGN_MAX_AGE here.
   *
   * The browser itself controls the cookie lifetime
   * through COOKIE_MAX_AGE.
   */
  function getCookieCampaign() {
    const offer =
      getCookie(COOKIE_NAME);

    const discount =
      getCookie(COOKIE_DISCOUNT_NAME);

    if (!offer || !discount) {
      return null;
    }

    return {
      offer,
      discount
    };
  }


  function saveCampaignCookies(
    offer,
    discount
  ) {
    setCookie(
      COOKIE_NAME,
      offer,
      COOKIE_MAX_AGE
    );

    setCookie(
      COOKIE_DISCOUNT_NAME,
      discount,
      COOKIE_MAX_AGE
    );
  }


  function deleteCampaignCookies() {
    deleteCookie(COOKIE_NAME);
    deleteCookie(COOKIE_DISCOUNT_NAME);
  }


  // --------------------------------------------------
  // SHOPIFY NATIVE DISCOUNT
  // --------------------------------------------------

  /*
   * Shopify creates/updates the "discount_code" cookie
   * when the customer enters the shop through:
   *
   * /discount/TEST209?offer=kochpinzette
   *
   * Example:
   *
   * discount_code=TEST209
   *
   * We deliberately DO NOT use this cookie as a
   * campaign on its own.
   *
   * It is only used when a new ?offer=... parameter
   * is present.
   */
  function getShopifyDiscountCode() {
    const discount =
      getCookie(
        SHOPIFY_DISCOUNT_COOKIE
      );

    return discount
      ? discount.trim()
      : null;
  }


  // --------------------------------------------------
  // CART
  // --------------------------------------------------

  async function getOfferCartData() {
    try {
      const response =
        await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        });

      if (!response.ok) {
        throw new Error(
          `Cart request failed with status ${response.status}`
        );
      }

      const cart =
        await response.json();

      const channel =
        cart.attributes?.[
          CART_ATTRIBUTE_NAME
        ]?.trim() || null;

      const discount =
        cart.attributes?.[
          CART_ATTRIBUTE_DISCOUNT
        ]?.trim() || null;

      const startedAt =
        cart.attributes?.[
          CART_ATTRIBUTE_STARTED_AT
        ]?.trim() || null;

      return {
        channel,
        discount,
        startedAt
      };

    } catch (error) {
      console.error(
        '[ShopOffer] Cart could not be loaded:',
        error
      );

      return {
        channel: null,
        discount: null,
        startedAt: null
      };
    }
  }


  /*
   * Only the CART campaign is checked against
   * CAMPAIGN_MAX_AGE.
   */
  function isCampaignStillValid(
    startedAt
  ) {
    if (!startedAt) {
      return false;
    }

    const startedAtTimestamp =
      new Date(
        startedAt
      ).getTime();

    if (
      Number.isNaN(
        startedAtTimestamp
      )
    ) {
      return false;
    }

    const age =
      Date.now() -
      startedAtTimestamp;

    return (
      age >= 0 &&
      age < CAMPAIGN_MAX_AGE
    );
  }


  async function updateOfferCartAttribute(
    offer,
    discount,
    startedAt
  ) {
    window.ShopOfferCartUpdating = true;

    try {
      await fetch(
        '/cart/update.js',
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json'
          },
          body: JSON.stringify({
            attributes: {
              [CART_ATTRIBUTE_NAME]:
                offer || '',

              [CART_ATTRIBUTE_DISCOUNT]:
                discount || '',

              [CART_ATTRIBUTE_STARTED_AT]:
                startedAt || ''
            }
          })
        }
      );
    } catch (error) {
      console.error(
        '[ShopOffer] Cart attributes could not be updated:',
        error
      );
    } finally {
      window.ShopOfferCartUpdating = false;
    }
  }


  function clearCartCampaign() {
    setTimeout(() => {
      updateOfferCartAttribute(
        '',
        '',
        ''
      );
    }, 500);
  }


  // --------------------------------------------------
  // DISCOUNT RESTORATION
  // --------------------------------------------------

  /*
   * Used only when:
   *
   * - the campaign comes from our cookie
   * - the current cart no longer contains the campaign
   *
   * Shopify then gets the native discount again.
   */
  function restoreShopifyDiscount(
    discount
  ) {
    if (!discount) {
      return;
    }


    /*
     * If the current URL is already a Shopify
     * discount URL for this discount, nothing
     * needs to be restored.
     */
    const pathname =
      window.location.pathname;

    const match =
      pathname.match(
        /^\/discount\/([^/?#]+)/i
      );


    if (match) {
      try {
        const currentDiscount =
          decodeURIComponent(
            match[1]
          ).trim();

        if (
          currentDiscount === discount
        ) {
          return;
        }
      } catch (error) {
        // Continue with restoration.
      }
    }


    /*
     * Remember the exact page the customer
     * is currently visiting.
     */
    const returnUrl =
      window.location.pathname +
      window.location.search +
      window.location.hash;


    /*
     * Create Shopify Native Discount URL.
     *
     * Example:
     *
     * /discount/TEST239?redirect=/products/foo
     */
    const discountUrl =
      new URL(
        `/discount/${encodeURIComponent(
          discount
        )}`,
        window.location.origin
      );


    discountUrl.searchParams.set(
      'redirect',
      returnUrl
    );


    window.location.href =
      discountUrl.toString();
  }


  // --------------------------------------------------
  // OFFER VISIBILITY
  // --------------------------------------------------

  function updateOfferVisibility(
    activeChannel
  ) {
    const elements =
      document.querySelectorAll(
        '[data-offer-channel]'
      );

    elements.forEach(
      (element) => {
        const requiredChannel =
          element.dataset.offerChannel?.trim();

        /*
         * No campaign configured on the element.
         */
        if (!requiredChannel) {
          element.hidden = false;
          return;
        }

        /*
         * Only show the element if the campaign
         * matches exactly.
         */
        element.hidden =
          !activeChannel ||
          requiredChannel !==
            activeChannel;
      }
    );
  }


  // --------------------------------------------------
  // SHOP OFFER STATE
  // --------------------------------------------------

  function updateShopOfferState(
    activeChannel
  ) {
    const hasSitewideOffer =
      document.querySelector(
        '[data-offer-type="sitewide"]'
      ) !== null;

    document.body.classList.toggle(
      'shop-offer-utm-active',
      Boolean(activeChannel)
    );

    window.ShopOffer = {
      channel: {
        active:
          Boolean(activeChannel),

        id:
          activeChannel || null
      },

      sitewide: {
        active:
          hasSitewideOffer
      }
    };

    updateOfferVisibility(
      activeChannel
    );
  }


  // --------------------------------------------------
  // INITIALIZATION
  // --------------------------------------------------

  async function init() {

    const urlOffer =
      getUrlOffer();

    const hasConsent =
      hasOfferCookieConsent();


    let activeCampaign = null;
    let campaignSource = null;


    // ==================================================
    // 1. NEW URL CAMPAIGN
    // ==================================================

    /*
     * The external campaign URL is:
     *
     * /discount/TEST209?offer=kochpinzette
     *
     * Shopify handles the native discount and then
     * redirects to the shop while keeping:
     *
     * ?offer=kochpinzette
     *
     * Shopify also sets:
     *
     * discount_code=TEST209
     *
     * Therefore:
     *
     * offer    = URL parameter
     * discount = Shopify cookie
     *
     * IMPORTANT:
     * This block always has priority over an
     * existing campaign.
     *
     * Therefore a new URL campaign replaces:
     *
     * old offer + old discount
     *
     * with:
     *
     * new offer + new discount
     */
    if (urlOffer) {

      const shopifyDiscount =
        getShopifyDiscountCode();


      /*
       * We only create a new campaign when
       * Shopify has provided a discount code.
       */
      if (shopifyDiscount) {

        const startedAt =
          new Date().toISOString();

        activeCampaign = {
          offer:
            urlOffer,

          discount:
            shopifyDiscount,

          startedAt
        };

        campaignSource =
          'url';


        /*
         * Always persist the new campaign
         * in the cart.
         *
         * This does NOT require cookie consent.
         */
        setTimeout(() => {
          updateOfferCartAttribute(
            activeCampaign.offer,
            activeCampaign.discount,
            activeCampaign.startedAt
          );
        }, 500);


        /*
         * Persist in our own cookies only
         * when consent is available.
         */
        if (hasConsent) {
          saveCampaignCookies(
            activeCampaign.offer,
            activeCampaign.discount
          );
        }
      }
    }


    // ==================================================
    // 2. COOKIE
    // ==================================================

    /*
     * No URL campaign:
     *
     * If our own campaign cookie exists,
     * it is the primary persistence mechanism.
     */
    if (
      !activeCampaign &&
      hasConsent
    ) {

      const cookieCampaign =
        getCookieCampaign();

      if (cookieCampaign) {

        activeCampaign = {
          offer:
            cookieCampaign.offer,

          discount:
            cookieCampaign.discount
        };

        campaignSource =
          'cookie';
      }
    }


    // ==================================================
    // 3. CART FALLBACK
    // ==================================================

    /*
     * Only use the cart if there is:
     *
     * - no URL campaign
     * - no cookie campaign
     */
    if (!activeCampaign) {

      const cartData =
        await getOfferCartData();


      /*
       * Valid cart campaign.
       */
      if (
        cartData.channel &&
        cartData.discount &&
        isCampaignStillValid(
          cartData.startedAt
        )
      ) {

        activeCampaign = {
          offer:
            cartData.channel,

          discount:
            cartData.discount,

          startedAt:
            cartData.startedAt
        };

        campaignSource =
          'cart';


        /*
         * If consent is available, promote the
         * cart campaign into our cookies.
         */
        if (hasConsent) {
          saveCampaignCookies(
            activeCampaign.offer,
            activeCampaign.discount
          );
        }
      }


      /*
       * Cart campaign exists but has expired
       * or is incomplete.
       *
       * Do NOT delete cookies here.
       *
       * At this point there is no active cookie,
       * otherwise we would never have reached
       * this block.
       */
      else if (
        cartData.channel ||
        cartData.discount ||
        cartData.startedAt
      ) {

        clearCartCampaign();
      }
    }


    // ==================================================
    // 4. NO CAMPAIGN
    // ==================================================

    if (!activeCampaign) {

      /*
       * No URL campaign,
       * no campaign cookie,
       * no valid cart campaign.
       *
       * Therefore it is safe to remove stale
       * campaign cookies.
       */
      if (hasConsent) {
        deleteCampaignCookies();
      }

      updateShopOfferState(
        null
      );

      return;
    }


    // ==================================================
    // 5. COOKIE RESTORATION
    // ==================================================

    /*
     * When the campaign comes from our cookie,
     * check whether the current cart still contains
     * the same campaign.
     */
    if (
      campaignSource === 'cookie'
    ) {

      const cartData =
        await getOfferCartData();


      const hasSameValidCartCampaign =
        cartData.channel ===
          activeCampaign.offer &&

        cartData.discount ===
          activeCampaign.discount &&

        isCampaignStillValid(
          cartData.startedAt
        );


      /*
       * Same valid cart:
       *
       * Shopify already has the campaign cart.
       *
       * Do NOT touch the discount.
       */
      if (
        hasSameValidCartCampaign
      ) {

        updateShopOfferState(
          activeCampaign.offer
        );

        return;
      }


      /*
       * Cookie exists but the cart campaign
       * is missing or expired.
       *
       * Rebuild the campaign cart.
       */
      const startedAt =
        new Date().toISOString();


      setTimeout(() => {
        updateOfferCartAttribute(
          activeCampaign.offer,
          activeCampaign.discount,
          startedAt
        );
      }, 500);


      /*
       * Restore the Shopify Native Discount.
       *
       * Shopify will process:
       *
       * /discount/TEST239?redirect=...
       */
      restoreShopifyDiscount(
        activeCampaign.discount
      );
    }


    // ==================================================
    // 6. OFFER STATE
    // ==================================================

    updateShopOfferState(
      activeCampaign.offer
    );

    // console.log('[ShopOffer]', {
    //   source: campaignSource,
    //   campaign: activeCampaign
    // });
  }


  // --------------------------------------------------
  // COOKIEBOT EVENTS
  // --------------------------------------------------

  /*
   * Cookiebot fires this event after consent
   * has been accepted.
   */
  window.addEventListener(
    'CookiebotOnAccept',
    function () {
      init();
    }
  );


  /*
   * If consent is declined or withdrawn:
   *
   * - remove our campaign cookies
   * - keep cart attributes
   *
   * The cart is the consent-independent
   * persistence mechanism.
   */
  window.addEventListener(
    'CookiebotOnDecline',
    async function () {

      deleteCampaignCookies();


      /*
       * The cart may still contain a valid campaign.
       */
      const cartData =
        await getOfferCartData();


      let activeChannel =
        null;


      if (
        cartData.channel &&
        cartData.discount &&
        isCampaignStillValid(
          cartData.startedAt
        )
      ) {
        activeChannel =
          cartData.channel;
      }


      updateShopOfferState(
        activeChannel
      );
    }
  );


  // --------------------------------------------------
  // START
  // --------------------------------------------------

  init();

  /*
  * Shopify may replace Liquid-rendered sections
  * after cart changes.
  *
  * Whenever an offer element is inserted/replaced,
  * re-apply the current ShopOffer state.
  */
  const shopOfferObserver =
    new MutationObserver((mutations) => {
      if (
        !window.ShopOffer?.channel?.active
      ) {
        return;
      }

    const offerElementChanged =
      mutations.some((mutation) => {
        if (mutation.type !== 'childList') {
          return false;
        }

        if (
          [...mutation.addedNodes].some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (
                node.matches?.(
                  '[data-offer-channel]'
                ) ||
                node.querySelector?.(
                  '[data-offer-channel]'
                )
              )
          )
        ) {
          return true;
        }

        return false;
      });

    if (!offerElementChanged) {
      return;
    }

    updateOfferVisibility(
      window.ShopOffer.channel.id
    );
  });

  shopOfferObserver.observe(
    document.body,
    {
      childList: true,
      subtree: true
    }
  );

})();
